import { supabase } from '../../lib/supabase';

import type { EventColor } from './activity-format';
import type { BoardTemplate, StageKind } from './workflow-board';

/**
 * Phase 140 RPC surface for workflow boards.
 *
 * Same untyped escape hatch as `activity-api.ts` and `documents-api.ts` — the
 * generated `src/types/database.ts` predates all three.
 */
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export interface BoardSummary {
  id: string;
  name: string;
  description: string | null;
  archived_at: string | null;
  created_at: string;
  card_count: number;
  stage_count: number;
}

export interface BoardStage {
  id: string;
  name: string;
  color: EventColor;
  position: number;
  wip_limit: number | null;
  kind: StageKind;
  auto_event_type_id: string | null;
  auto_event_label: string | null;
  /* ---- phase 146 ---- */
  auto_advance: boolean;
  auto_advance_to_id: string | null;
  /** Names of the checklist packs attached here, in order. */
  checklist_packs: string[];
  extra_item_count: number;
  /* ---- phase 148 ---- */
  rule_count: number;
}

export interface BoardCard {
  id: string;
  stage_id: string;
  student_id: string;
  position: number;
  due_at: string | null;
  entered_stage_at: string;
  assignee_id: string | null;
  assignee_name: string | null;
  student_name: string;
  roll_no: string | null;
  exam_date: string | null;
  /* ---- phase 145: checklist progress on the current visit ---- */
  required_total: number;
  required_done: number;
  item_total: number;
  item_done: number;
  overdue_items: number;
  /** Every required item done, and there is at least one. */
  ready: boolean;
  /* ---- phase D: whose move it is, and what the move is ---- */
  /** Open lines the student owes us. */
  with_student: number;
  /** Open lines waiting on something neither of us controls. */
  waiting_items: number;
  /** Open lines somebody in the office could pick up right now. */
  our_items: number;
  /** The most urgent of those, named. Null when none of it is ours. */
  next_label: string | null;
  next_due_at: string | null;
  /* ---- phase 148: custom field values, keyed by the field's `key` ---- */
  fields: Record<string, string | number | boolean | null>;
}

export interface BoardField {
  id: string;
  key: string;
  label: string;
  kind: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'member';
  options: string[];
  position: number;
}

export interface BoardDetail {
  board: {
    id: string;
    name: string;
    description: string | null;
    archived_at: string | null;
    /** Owner/admin. The RPCs re-check; this only hides dead controls. */
    can_configure: boolean;
  };
  stages: BoardStage[];
  cards: BoardCard[];
  /** Phase 148. Empty on a board that never added one. */
  fields: BoardField[];
}

/**
 * One board this student is on (phase K).
 *
 * Every field matches `BoardCard` deliberately, so the student's page and the
 * board it links to render through the same `boardCardNext` and cannot
 * describe one card differently.
 */
export interface StudentWorkflowCard {
  id: string;
  board_id: string;
  board_name: string;
  board_archived: boolean;
  stage_id: string;
  stage_name: string;
  stage_color: EventColor;
  stage_kind: StageKind;
  entered_stage_at: string;
  due_at: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  required_total: number;
  required_done: number;
  item_total: number;
  item_done: number;
  overdue_items: number;
  ready: boolean;
  with_student: number;
  waiting_items: number;
  our_items: number;
  next_label: string | null;
  next_due_at: string | null;
}

export const WORKFLOW_KEYS = {
  boards: (instituteId: string) => ['institute', 'boards', instituteId] as const,
  board: (boardId: string) => ['institute', 'board', boardId] as const,
  studentWorkflow: (studentId: string) => ['institute', 'student_workflow', studentId] as const,
};

/** Every board this student is on, live work first, then what has finished. */
export async function listStudentWorkflow(studentId: string): Promise<StudentWorkflowCard[]> {
  const { data, error } = await rpcUnknown.rpc('institute_student_workflow', {
    p_student_id: studentId,
  });
  if (error !== null) throw new Error(error.message);
  return Array.isArray(data) ? (data as StudentWorkflowCard[]) : [];
}

/**
 * `includeArchived` is what lets the boards page own the active/archived split
 * client-side, the way the roster and the drafts list already do. Default false
 * so nothing that only wants live boards has to say so.
 */
export async function listBoards(
  instituteId: string,
  includeArchived = false,
): Promise<BoardSummary[]> {
  const { data, error } = await rpcUnknown.rpc('institute_list_boards', {
    p_institute_id: instituteId,
    p_include_archived: includeArchived,
  });
  if (error !== null) throw new Error(error.message);
  return Array.isArray(data) ? (data as BoardSummary[]) : [];
}

export async function getBoard(boardId: string): Promise<BoardDetail> {
  const { data, error } = await rpcUnknown.rpc('institute_get_board', {
    p_board_id: boardId,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') throw new Error('no board');
  const detail = data as BoardDetail;
  // Phase D's four fields are filled in defensively: a deploy that lands before
  // the migration should degrade to "no summary", never to a board that throws
  // while reading `next_label` off two hundred cards.
  return {
    ...detail,
    cards: detail.cards.map((card) => ({
      ...card,
      with_student: card.with_student ?? 0,
      waiting_items: card.waiting_items ?? 0,
      our_items: card.our_items ?? 0,
      next_label: card.next_label ?? null,
      next_due_at: card.next_due_at ?? null,
    })),
  };
}

/**
 * `templateId` (phase 147) takes a saved template row directly. The literal
 * `template` names still work and now resolve to the seeded template codes, so
 * nothing that called the phase-140 signature has to change.
 */
export async function createBoard(
  instituteId: string,
  name: string,
  template: BoardTemplate,
  templateId: string | null = null,
): Promise<string> {
  const { data, error } = await rpcUnknown.rpc('institute_create_board', {
    p_institute_id: instituteId,
    p_name: name,
    p_template: template,
    p_template_id: templateId,
  });
  if (error !== null) throw new Error(error.message);
  return (data as { board_id?: string } | null)?.board_id ?? '';
}

/**
 * Rename, re-describe, archive or restore. The RPC rejects an empty name, so an
 * archive/restore call has to pass the board's current name and description
 * through rather than nulls.
 */
export async function updateBoard(
  boardId: string,
  name: string,
  description: string | null,
  archived: boolean,
): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_update_board', {
    p_board_id: boardId,
    p_name: name,
    p_description: description,
    p_archived: archived,
  });
  if (error !== null) throw new Error(error.message);
}

/**
 * Permanent, and it takes every card's stage history with it — the board is
 * gone, not hidden. Archiving via `updateBoard` is the reversible option.
 */
export async function deleteBoard(boardId: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_delete_board', { p_board_id: boardId });
  if (error !== null) throw new Error(error.message);
}

export interface UpsertStageInput {
  board_id: string;
  stage_id: string | null;
  name: string;
  color: EventColor;
  kind: StageKind;
  wip_limit: number | null;
  auto_event_type_id: string | null;
  /** Phase 146. An explicit destination, never "the next one by position". */
  auto_advance: boolean;
  auto_advance_to_id: string | null;
}

export async function upsertStage(input: UpsertStageInput): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_upsert_stage', {
    p_board_id: input.board_id,
    p_stage_id: input.stage_id,
    p_name: input.name,
    p_color: input.color,
    p_kind: input.kind,
    p_wip_limit: input.wip_limit,
    p_auto_event_type_id: input.auto_event_type_id,
    p_auto_advance: input.auto_advance,
    p_auto_advance_to_id: input.auto_advance_to_id,
  });
  if (error !== null) throw new Error(error.message);
}

/** Takes the complete order, so a fast double-click cannot half-apply. */
export async function reorderStages(boardId: string, stageIds: string[]): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_reorder_stages', {
    p_board_id: boardId,
    p_stage_ids: stageIds,
  });
  if (error !== null) throw new Error(error.message);
}

export async function deleteStage(stageId: string, moveToId: string | null): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_delete_stage', {
    p_stage_id: stageId,
    p_move_to_id: moveToId,
  });
  if (error !== null) throw new Error(error.message);
}

export async function addCard(
  boardId: string,
  studentId: string,
  stageId: string | null,
): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_add_card', {
    p_board_id: boardId,
    p_student_id: studentId,
    p_stage_id: stageId,
  });
  if (error !== null) throw new Error(error.message);
}

/**
 * `afterCardId` is the card this one should land immediately after; null means
 * the top of the column. The position itself is the server's to compute — see
 * decision 1 in the migration header.
 */
export async function moveCard(
  cardId: string,
  stageId: string,
  afterCardId: string | null,
): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_move_card', {
    p_card_id: cardId,
    p_stage_id: stageId,
    p_after_card_id: afterCardId,
  });
  if (error !== null) throw new Error(error.message);
}

export async function updateCard(
  cardId: string,
  assigneeId: string | null,
  dueAt: string | null,
): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_update_card', {
    p_card_id: cardId,
    p_assignee_id: assigneeId,
    p_due_at: dueAt,
  });
  if (error !== null) throw new Error(error.message);
}

export async function archiveCard(cardId: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_archive_card', { p_card_id: cardId });
  if (error !== null) throw new Error(error.message);
}

/* ---------------------------------------------------------- phase 141 -- */

export interface StageInsight {
  id: string;
  name: string;
  color: EventColor;
  kind: StageKind;
  position: number;
  live_cards: number;
  /** Median days a card has historically spent here; null with no history. */
  median_days: number | null;
  entered_30d: number;
  /** Cards that left this stage straight to a 'lost' column. */
  lost_from_here: number;
  /* ---- phase 146 ---- */
  /** Median open required items across the cards sitting here. */
  median_open_items: number | null;
  /** The one item blocking the most cards — the bottleneck, named. */
  top_blocker: { label: string; cards: number } | null;
  auto_entries: number;
}

export interface BoardInsights {
  stages: StageInsight[];
  totals: {
    live: number;
    won: number;
    lost: number;
    /** Open cards sitting in one column longer than STALE_AFTER_DAYS. */
    stale: number;
    overdue: number;
  };
  /**
   * How much work the automation is actually doing. Near zero means the rules
   * are wrong, and that is worth knowing before anyone trusts them.
   */
  advance_mix: { auto: number; manual: number };
}

export async function getBoardInsights(boardId: string): Promise<BoardInsights> {
  const { data, error } = await rpcUnknown.rpc('institute_board_insights', {
    p_board_id: boardId,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') throw new Error('no insights');
  return data as BoardInsights;
}

/**
 * The six things the queue merges. Phase 174 added `review` (a file the
 * student sent that is waiting for someone to look at it); phase 177 added
 * `grading` (an attempt waiting for an examiner) and `assignment` (a student
 * who has not handed one in), which until then lived on their own pages and
 * so never appeared in the one list that claims to be the list.
 */
export type WorkKind =
  | 'follow_up'
  | 'card'
  | 'checklist'
  | 'review'
  | 'grading'
  | 'assignment'
  // Phase 179b. `message` is a thread whose newest STUDENT-VISIBLE message
  // came from the student; `task` is something asked of them in words.
  | 'message'
  | 'task';

/** The examiner-graded sections a `grading` row is still waiting on. */
export type WorkSkill = 'listening' | 'reading' | 'writing' | 'speaking';

export interface WorkItem {
  /** Which source it came from — drives the link, the icon and the actions. */
  kind: WorkKind;
  id: string;
  /** Set for 'card' and 'checklist' (and a 'review' raised from a card). */
  card_id: string | null;
  stage_id: string | null;
  student_id: string;
  student_name: string;
  /** Null only for an undated checklist item (see `includeUndated`). For a
   *  review it is when the file arrived. */
  due_at: string | null;
  title: string;
  subtitle: string | null;
  color: EventColor;
  icon: string | null;
  board_id: string | null;
  board_name: string | null;
  /** Null when nobody owns it — the RPC compares against NULL. */
  is_mine: boolean | null;
  is_unassigned: boolean;
  assignee_id: string | null;
  assignee_name: string | null;
  blocked_reason: string | null;
  blocked_until: string | null;
  /** An open request: the student has been asked and has not answered. */
  waiting_on_student: boolean;
  /** A submission is sitting in the review queue for this row's card/item. */
  submission_pending: boolean;
  /* ---- 'review' rows only ---- */
  document_id: string | null;
  document_kind: string | null;
  storage_path: string | null;
  external_url: string | null;
  request_id: string | null;
  /* ---- phase 177 ---- */
  /** Which sections a 'grading' row still needs marked. Empty on every other
   *  kind, and empty on a grading row that only awaits approval. */
  skills: WorkSkill[];
  /** Set for 'grading' and 'assignment'; drives the batch filter. */
  batch_id: string | null;
  batch_name: string | null;
  /** 'grading' only — where the row opens. */
  attempt_id: string | null;
  /** 'assignment' always; 'grading' when the attempt came from one. */
  assignment_id: string | null;
}

export const WORK_KEYS = {
  myWork: (instituteId: string, mine: boolean, includeUndated = false) =>
    ['institute', 'my_work', instituteId, mine, includeUndated] as const,
  insights: (boardId: string) => ['institute', 'board_insights', boardId] as const,
};

/**
 * `includeUndated` adds open checklist items with no date — only ever for the
 * caller's own work (the RPC ignores it otherwise), because the whole
 * institute's undated items would bury the dated work the queue exists for.
 */
export async function listMyWork(
  instituteId: string,
  mine: boolean,
  includeUndated = false,
  limit = 60,
): Promise<WorkItem[]> {
  const { data, error } = await rpcUnknown.rpc('institute_my_work', {
    p_institute_id: instituteId,
    p_mine: mine,
    p_limit: limit,
    p_include_undated: includeUndated,
  });
  if (error !== null) throw new Error(error.message);
  if (!Array.isArray(data)) return [];
  // `skills` is read with `.includes()` by the skill filter, so it must be an
  // array even when the phase-177 migration has not been applied yet — a
  // deploy that lands before the SQL should degrade to "no skills", never to a
  // TypeError on every render.
  return (data as WorkItem[]).map((row) => ({
    ...row,
    skills: Array.isArray(row.skills) ? row.skills : [],
    batch_id: row.batch_id ?? null,
    batch_name: row.batch_name ?? null,
    attempt_id: row.attempt_id ?? null,
    assignment_id: row.assignment_id ?? null,
  }));
}

/**
 * Reassign a queue row in place. For a 'review' row pass the REQUEST id, not
 * the document id — an unprompted upload has no request and cannot be owned.
 * For a 'grading' row pass the ATTEMPT id. 'assignment' rows have no owner of
 * their own and the RPC rejects them.
 */
export async function assignWorkItem(
  kind: WorkKind,
  id: string,
  assigneeId: string | null,
): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_assign_work_item', {
    p_kind: kind,
    p_id: id,
    p_assignee_id: assigneeId,
  });
  if (error !== null) throw new Error(error.message);
}

/** Move the date; keep everything else. Reviews cannot be snoozed. */
export async function snoozeWorkItem(kind: WorkKind, id: string, dueAt: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_snooze_work_item', {
    p_kind: kind,
    p_id: id,
    p_due_at: dueAt,
  });
  if (error !== null) throw new Error(error.message);
}
